import { ApolloError } from '@apollo/client';

import { useApiGatewayQuery } from '~hooks';
import { PRODUCTCATEGORY, PRODUCTCATEGORY_BY_ID } from '~queries';
import {
  GetProductCategoryByIdQuery,
  GetProductCategoryByIdQueryVariables,
  GetProductCategoryQuery,
  GetProductCategoryQueryVariables,
  ProductCategory,
} from '~types/appsync.types';
import { parseDates } from '~utils';

interface UseProductCategoryOptions {
  productCategoryId?: number;
  productCategoryShortname?: string;
  validityCategoryId?: number;
}

const useProductCategory = (
  options: UseProductCategoryOptions = {},
): [ProductCategory | undefined, boolean, ApolloError | undefined] => {
  const { productCategoryId, productCategoryShortname, validityCategoryId } = options;

  // Note: Using !== null to check for both undefined and null, while allowing 0 as a valid ID
  const hasProductCategoryId = productCategoryId !== undefined && productCategoryId !== null;

  const byIdResult = useApiGatewayQuery<
    GetProductCategoryByIdQuery,
    GetProductCategoryByIdQueryVariables
  >(PRODUCTCATEGORY_BY_ID, {
    skip: !hasProductCategoryId,
    variables: { productCategoryId: productCategoryId!, validityCategoryId },
  });

  const byShortnameResult = useApiGatewayQuery<
    GetProductCategoryQuery,
    GetProductCategoryQueryVariables
  >(PRODUCTCATEGORY, {
    skip: hasProductCategoryId || !productCategoryShortname,
    variables: { productCategoryShortname: productCategoryShortname!, validityCategoryId },
  });

  if (hasProductCategoryId) {
    const raw = byIdResult.data?.productCategoryById;
    const productCategory = raw
      ? parseDates<ProductCategory>(raw, ['validFrom', 'validTo'])
      : undefined;
    return [productCategory, byIdResult.loading, byIdResult.error];
  }

  const raw = byShortnameResult.data?.productCategory;
  const productCategory = raw
    ? parseDates<ProductCategory>(raw, ['validFrom', 'validTo'])
    : undefined;

  return [productCategory, byShortnameResult.loading, byShortnameResult.error];
};

export default useProductCategory;
